import {
  BoundSelectCardGroupField,
  BoundSelectField,
  Button,
  Css,
  FormLines,
  FormSectionLayout,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useMemo, useState } from "react";
import { SearchBox } from "src/components";
import { useRouteParams } from "src/hooks/useRouteParams";
import { ProjectParams } from "src/routes/routesDef";
import { extractPoMessages, formatCentsToPrice, noop, queryResult } from "src/utils";
import {
  SelectOptionStep_PotentialOptionFragment,
  SelectOptionStep_PotentialOptionGroupFragment,
  useSelectOptionStepQuery,
} from "./1.SelectOptionStep.generated";
import { AddOrModifyOptionsFormInput } from "./addOrModifyOptionsWorkflow";

type SelectOptionStepProps = { formState: ObjectState<AddOrModifyOptionsFormInput> };
export function SelectOptionStep(props: SelectOptionStepProps) {
  const { formState } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const query = useSelectOptionStepQuery({ variables: { projectId } });
  return queryResult(query, ({ projectReadyPlanConfig: prpc }) => (
    <FormSectionLayout
      title="Select an Existing Option to Add"
      description="Select an existing product offering option to add to this project."
      // The whole step is one unlabeled block of fields, so it goes above the (empty) section list.
      initialFields={
        <OptionSelector
          formState={formState}
          potentialOptionGroups={prpc.potentialOptionGroups}
          offeringVersion={prpc.productOffering?.aggregateCheckout?.version ?? undefined}
        />
      }
      sections={[]}
    />
  ));
}

type OptionSelectorProps = {
  formState: ObjectState<AddOrModifyOptionsFormInput>;
  potentialOptionGroups: SelectOptionStep_PotentialOptionGroupFragment[];
  offeringVersion: number | undefined;
};
function OptionSelector(props: OptionSelectorProps) {
  const { formState, potentialOptionGroups, offeringVersion } = props;
  const [search, setSearch] = useState("");

  const { addOnCards, personalizationOptions, personalizationDisabledOptions } = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    const matchesSearch = (po: SelectOptionStep_PotentialOptionFragment) =>
      !searchTerm ||
      po.option.maybeShortName.toLowerCase().includes(searchTerm) ||
      po.option.code.toLowerCase().includes(searchTerm);
    // Filter out groups the project already has an option from.
    const groups = potentialOptionGroups
      .filter((g) => !g.options.some((po) => po.isSelected))
      .sortBy((g) => g.group.order);

    // Add-ons render as radio cards; disabled ones stay visible with a tooltip explaining why.
    const addOnCards = groups
      .filter(isAddOnOrStructural)
      .flatMap((g) => g.options)
      .filter(matchesSearch)
      .map((po) => ({
        value: po.option.id,
        label: po.option.maybeShortName,
        description: <OptionCardDetail potentialOption={po} />,
        disabled: !po.canBeSelected.allowed,
        tooltip: extractPoMessages(po.canBeSelected),
      }));

    // Personalization options collapse into a single dropdown, labeled by their option group.
    const personalizations = groups
      .filter(isPersonalization)
      .flatMap((g) => g.options.filter(matchesSearch).map((po) => ({ po, groupName: g.group.name })));
    const personalizationOptions = personalizations.map(({ po, groupName }) => ({
      id: po.option.id,
      name: po.option.maybeShortName,
      groupName,
    }));
    // Disabled personalization options are shown greyed-out with the reason as a tooltip.
    const personalizationDisabledOptions = personalizations
      .filter(({ po }) => !po.canBeSelected.allowed)
      .map(({ po }) => ({ value: po.option.id, reason: extractPoMessages(po.canBeSelected) ?? "" }));

    return { addOnCards, personalizationOptions, personalizationDisabledOptions };
  }, [potentialOptionGroups, search]);

  const hasOptions = addOnCards.nonEmpty || personalizationOptions.nonEmpty;

  return (
    <div css={Css.df.fdc.gap3.$}>
      {/* Wizard-step search stays out of the URL: writing `?search=` does a router-level replace that
          would wipe the draft's edit URL that step 1 just put in the address bar. */}
      <SearchBox onSearch={setSearch} updateQueryString={false} />
      {hasOptions ? (
        <>
          <p css={Css.sm.gray700.$}>
            Showing options available in the current product offering. Options already included in this project are
            hidden.
          </p>
          {/* Both inputs bind to selectedOptionId, so picking in one clears the other (single selection). */}
          {personalizationOptions.nonEmpty && (
            <BoundSelectField
              data-testid="personalizationOption"
              label="Personalization"
              options={personalizationOptions}
              field={formState.selectedOptionId}
              getOptionMenuLabel={(o) => `${o.groupName} — ${o.name}`}
              nothingSelectedText="Select a personalization option"
              disabledOptions={personalizationDisabledOptions}
            />
          )}
          {addOnCards.nonEmpty && (
            <BoundSelectCardGroupField
              view="list"
              labelStyle="hidden"
              options={addOnCards}
              field={formState.selectedOptionId}
            />
          )}
        </>
      ) : (
        <div css={Css.df.fdc.aic.jcc.gap3.py7.$}>
          <div css={Css.sm.gray700.tac.$}>
            No matching options found.
            {offeringVersion !== undefined && ` This project uses Product Offering Version ${offeringVersion}.`} Some
            newer options may not be available.
          </div>
          {/* TODO: Add link to True-Up Configuration workflow */}
          <Button label="True-Up Configuration" onClick={noop} variant="secondaryBlack" />
        </div>
      )}
    </div>
  );
}

function OptionCardDetail(props: { potentialOption: SelectOptionStep_PotentialOptionFragment }) {
  const { potentialOption } = props;
  const { option, totalCostInCents } = potentialOption;
  return (
    <FormLines width="lg" gap={1}>
      <span css={Css.xs.gray700.$}>{option.location?.pathArray.join(" > ")}</span>
      <span css={Css.xs.gray700.$}>{option.code}</span>
      <span css={Css.xs.gray700.$}>{totalCostInCents ? formatCentsToPrice(totalCostInCents) : ""}</span>
    </FormLines>
  );
}

// Add-on and structural groups render as radio cards.
function isAddOnOrStructural(pog: SelectOptionStep_PotentialOptionGroupFragment): boolean {
  const { type } = pog.group.group;
  return !!type.isAddOnV2 || !!type.isStructuralV2;
}

// Product / package groups render in the personalization dropdown.
function isPersonalization(pog: SelectOptionStep_PotentialOptionGroupFragment): boolean {
  const { type } = pog.group.group;
  return type.isProductOptionV2 || type.isPackageOptionV2;
}
